const initializeJenhepeSlider = (root) => {
  if (root.dataset.jhReady === 'true') return;
  root.dataset.jhReady = 'true';

  const viewport = root.querySelector('[data-jh-viewport]');
  const slides = [...root.querySelectorAll('[data-jh-slide]')];
  const dots = [...root.querySelectorAll('[data-jh-dot]')];
  const isStack = root.dataset.jhMode === 'stack';
  if (!viewport || slides.length === 0) return;

  let active = 0;
  let timer = null;
  let scrollFrame = null;
  let isVisible = false;
  let isInteracting = false;

  const updateControls = (index) => {
    active = (index + slides.length) % slides.length;
    slides.forEach((slide, slideIndex) => {
      const selected = slideIndex === active;
      slide.classList.toggle('is-active', selected);
      slide.setAttribute('aria-hidden', String(!selected));
      if (isStack) {
        let distance = slideIndex - active;
        if (distance > slides.length / 2) distance -= slides.length;
        if (distance < -slides.length / 2) distance += slides.length;
        slide.style.setProperty('--jh-position', String(distance));
        slide.style.setProperty('--jh-depth', String(Math.abs(distance)));
        slide.classList.toggle('is-neighbour', Math.abs(distance) === 1);
      }
    });
    dots.forEach((dot, dotIndex) => {
      const selected = dotIndex === active;
      dot.classList.toggle('is-active', selected);
      dot.setAttribute('aria-selected', String(selected));
    });
  };

  const horizontalPosition = (slide) => (
    slide.offsetLeft - ((viewport.clientWidth - slide.clientWidth) / 2)
  );

  const goTo = (index, behavior = 'smooth') => {
    const next = (index + slides.length) % slides.length;
    updateControls(next);
    if (!isStack) viewport.scrollTo({ left: horizontalPosition(slides[next]), top: 0, behavior });
  };

  const stop = () => {
    if (timer !== null) window.clearInterval(timer);
    timer = null;
  };

  const start = () => {
    stop();
    if (
      root.dataset.autoplay !== 'true' ||
      !isVisible ||
      isInteracting ||
      document.hidden ||
      slides.length < 2 ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) return;

    timer = window.setInterval(() => goTo(active + 1), Number(root.dataset.speed) || 6000);
  };

  const manualGoTo = (index) => {
    stop();
    goTo(index);
    if (!isInteracting) start();
  };

  root.querySelector('[data-jh-prev]')?.addEventListener('click', (event) => {
    event.preventDefault();
    manualGoTo(active - 1);
  });
  root.querySelector('[data-jh-next]')?.addEventListener('click', (event) => {
    event.preventDefault();
    manualGoTo(active + 1);
  });
  dots.forEach((dot, index) => dot.addEventListener('click', (event) => {
    event.preventDefault();
    manualGoTo(index);
  }));

  if (!isStack) viewport.addEventListener('scroll', () => {
    if (scrollFrame !== null) return;
    scrollFrame = window.requestAnimationFrame(() => {
      scrollFrame = null;
      const center = viewport.scrollLeft + (viewport.clientWidth / 2);
      const nearest = slides.reduce((best, slide, index) => {
        const distance = Math.abs((slide.offsetLeft + (slide.clientWidth / 2)) - center);
        return distance < best.distance ? { index, distance } : best;
      }, { index: active, distance: Number.POSITIVE_INFINITY });
      updateControls(nearest.index);
    });
  }, { passive: true });

  if (isStack) {
    let pointerStart = null;
    viewport.addEventListener('pointerdown', (event) => {
      pointerStart = event.clientX;
      viewport.setPointerCapture?.(event.pointerId);
      viewport.classList.add('is-dragging');
      stop();
    });
    viewport.addEventListener('pointerup', (event) => {
      if (pointerStart === null) return;
      const movement = event.clientX - pointerStart;
      pointerStart = null;
      viewport.classList.remove('is-dragging');
      if (Math.abs(movement) > 45) manualGoTo(active + (movement < 0 ? 1 : -1));
      else start();
    });
    viewport.addEventListener('pointercancel', () => {
      pointerStart = null;
      viewport.classList.remove('is-dragging');
      start();
    });
    slides.forEach((slide, index) => slide.addEventListener('click', (event) => {
      if (index === active) return;
      event.preventDefault();
      manualGoTo(index);
    }));
  }

  root.addEventListener('pointerenter', () => {
    isInteracting = true;
    stop();
  });
  root.addEventListener('pointerleave', () => {
    isInteracting = false;
    start();
  });
  root.addEventListener('focusin', () => {
    isInteracting = true;
    stop();
  });
  root.addEventListener('focusout', (event) => {
    if (root.contains(event.relatedTarget)) return;
    isInteracting = false;
    start();
  });
  document.addEventListener('visibilitychange', start);

  const visibilityObserver = new IntersectionObserver(([entry]) => {
    isVisible = entry.isIntersecting && entry.intersectionRatio >= 0.15;
    start();
  }, { threshold: [0, 0.15] });
  visibilityObserver.observe(root);

  updateControls(0);
};

document.querySelectorAll('[data-jh-slider]').forEach(initializeJenhepeSlider);
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-jh-slider]').forEach(initializeJenhepeSlider);
});
